/**
 * Os espaços e as abas, desenhados do mesmo jeito na mesa e nas janelas próprias
 * (docs/spec/room-layout.md e docs/spec/popout-windows.md).
 *
 * - Como no original, os módulos ficam em cartões separados por vãos, com puxadores para
 *   redimensionar, e sem título por cartão.
 * - O alto de cada espaço é a faixa de abas, de módulos quaisquer: as abas (com o ícone do módulo),
 *   o "+", a parte vazia (que arrasta o espaço inteiro; o botão direito nela abre o menu do espaço),
 *   "Recolher" e "Mais ações". As abas e os espaços se arrastam a qualquer hora, dentro da janela.
 * - Ctrl+E liga o modo de edição: o "+" põe uma aba num lugar, na borda de cada espaço e da janela.
 * - Numa janela estreita, as abas ficam todas juntas (o "acordeão" do original).
 * - O que muda o layout é uma operação (`LayoutOp`, em `room/layout.ts`): na mesa, ela vale na hora;
 *   numa janela própria, vai para a janela principal, que é quem grava o layout.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { sessionRules } from '../shared.js';
import { useApp } from '../store.js';
import { Icon } from '../ui/icons.js';
import { EmptyState } from '../ui/EmptyState.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import {
  CLONABLE,
  SITE_MIN,
  besideSide,
  dragSiteSize,
  findContent,
  findTab,
  listPanels,
  listSheets,
  newLayoutId,
  sheetPanel,
  sideOf,
  spacesByDesk,
  type ContentNode,
  type DesktopLayout,
  type DockNode,
  type DockPlace,
  type DropSide,
  type DropTarget,
  type LayoutOp,
  type ModuleClassId,
  type Sheet,
  type SheetTarget,
} from './layout.js';
import { zoneIn, zoneRect, type Rect } from './layoutDrag.js';
import { MODULES, SheetTitle, TabLabel, sheetText, tableName } from './modules/registry.js';
import { TabList, fullTitle } from './TabList.js';
import { ClockPill } from './clock/ClockPill.js';
import { openPopout } from './windows.js';

/** abaixo disso, a janela mostra um módulo por vez, com abas (o "acordeão" do original) */
const ACCORDION_BELOW = 720;
/** largura mínima do centro (o chat) antes de encolher as laterais; com mais espaços lado a lado, a soma deles */
const CENTER_MIN = 360;
/** o chat não fica mais estreito que isso (como no original), e os outros espaços abertos, lado a lado, que `PANEL_MIN` */
const CHAT_MIN = 318;
const PANEL_MIN = 240;
/** menor largura de uma lateral encolhida */
const SIDE_MIN = 180;
/** o vão entre os espaços (a área que pega o mouse passa 4 px por cima de cada um, em `room.css`) */
const SPLITTER = 4;
/** quanto o ponteiro anda antes de a aba, ou o espaço, começar a ser arrastado (abaixo disso, é um clique) */
const DRAG_START = 5;
/** o destaque da coluna (ou faixa) que nasce ao soltar na borda da mesa */
const EDGE_PREVIEW = { horizontal: 260, vertical: 180 } as const;

/**
 * Onde a aba (ou o espaço inteiro) arrastada cai. Dentro de um espaço, `before` é a aba que fica
 * depois dela (null: no fim), e o marcador fica entre as abas; `rect` destaca o espaço (ou o
 * pedaço) de destino.
 */
type Drop = { target: SheetTarget; before?: string | null; rect?: Rect; marker?: Rect };

const boxOf = (r: DOMRect): Rect => ({ left: r.left, top: r.top, width: r.width, height: r.height });

/**
 * A coluna (ou faixa) nova na borda da mesa: o destaque cobre o pedaço que ela vai ocupar. Numa
 * janela própria (`win`), a borda divide a janela, e o destaque cobre a metade daquele lado.
 */
function edgeDrop(side: DropSide, el: HTMLElement, win: string | null): Drop {
  if (win) return { target: { kind: 'edge', side, win }, rect: zoneRect(boxOf((el.closest('.rd-room__body') ?? el).getBoundingClientRect()), side) };
  const r = el.getBoundingClientRect();
  const box = boxOf(r);
  const w = Math.min(EDGE_PREVIEW.horizontal, window.innerWidth / 3);
  const h = Math.min(EDGE_PREVIEW.vertical, window.innerHeight / 3);
  const rect =
    side === 'left'
      ? { ...box, width: w }
      : side === 'right'
        ? { ...box, left: r.right - w, width: w }
        : side === 'top'
          ? { ...box, height: h }
          : { ...box, top: r.bottom - h, height: h };
  return { target: { kind: 'edge', side }, rect };
}

/**
 * O pedaço do conteúdo de um espaço perto de uma borda (até 30%), onde ele se divide. Enquanto
 * arrasta, o conteúdo não pega o ponteiro: vem pela caixa. O espaço recolhido não tem pedaço.
 */
function bodyZone(card: HTMLElement, x: number, y: number): DropSide | null {
  const body = card.querySelector(':scope > .rd-dock__body:not([hidden])')?.getBoundingClientRect();
  return body && y >= body.top ? zoneIn(boxOf(body), x, y) : null;
}

/**
 * O destino sob o ponteiro enquanto um espaço inteiro é arrastado pela parte vazia da faixa
 * (docs/spec/room-layout.md, "Mover um espaço inteiro"):
 * - a borda da janela e o centro vazio: o espaço vai para lá;
 * - a faixa de abas de outro espaço: as abas se juntam às dele, na posição;
 * - perto de uma borda do conteúdo de outro espaço: ele se divide, e o arrastado fica daquele lado;
 * - o miolo de outro espaço (ou ele recolhido): as abas se juntam às dele, no fim.
 * O próprio espaço não recebe nada; com cópia (`copy`), a da aba à vista vai para uma borda dele. O
 * espaço vazio só muda de lugar: não tem abas para juntar.
 */
function dropAt(x: number, y: number, from: ContentNode, copy: boolean, win: string | null): Drop | null {
  const merge = from.sheets.length > 0;
  for (const el of document.elementsFromPoint(x, y)) {
    if (!(el instanceof HTMLElement)) continue;
    const r = el.getBoundingClientRect();
    const side = el.dataset.dropEdge as DropSide | undefined;
    if (side) return edgeDrop(side, el, win);
    if (el.dataset.dropCenter !== undefined) return { target: { kind: 'center' }, rect: boxOf(r) };
    const strip = el.dataset.tabsOf;
    if (strip) return strip !== from.id && merge ? stripDrop(el, strip, x, '', false) : null;
    const nodeId = el.dataset.node;
    if (!nodeId) continue;
    const own = nodeId === from.id;
    const zone = own && !copy ? null : bodyZone(el, x, y);
    if (zone) return { target: { kind: 'split', nodeId, side: zone }, rect: zoneRect(boxOf(r), zone) };
    return !own && merge ? { target: { kind: 'into', nodeId }, before: null, rect: boxOf(r) } : null;
  }
  return null;
}

/**
 * A posição na faixa de abas sob o ponteiro, sem contar a aba arrastada (`sheetId`; vazio, com o
 * espaço inteiro, todas contam), e o marcador entre as abas.
 */
function stripDrop(strip: HTMLElement, nodeId: string, x: number, sheetId: string, own: boolean): Drop {
  const tabs = [...strip.querySelectorAll<HTMLElement>('[data-sheet]')].filter((tab) => tab.dataset.sheet !== sheetId);
  const index = tabs.findIndex((tab) => {
    const r = tab.getBoundingClientRect();
    return x < r.left + r.width / 2;
  });
  const s = strip.getBoundingClientRect();
  const next = index >= 0 ? tabs[index]!.getBoundingClientRect() : null;
  const last = tabs.length ? tabs[tabs.length - 1]!.getBoundingClientRect() : null;
  const left = next ? next.left - 2 : last ? last.right : s.left + 6;
  const card = strip.closest('[data-node]');
  return {
    target: { kind: 'into', nodeId },
    before: index >= 0 ? tabs[index]!.dataset.sheet! : null,
    ...(own || !card ? {} : { rect: boxOf(card.getBoundingClientRect()) }),
    marker: { left, top: s.top + 5, width: 2, height: Math.max(8, s.height - 10) },
  };
}

/**
 * O destino sob o ponteiro enquanto uma aba é arrastada (docs/spec/room-layout.md, "Arrastar as
 * abas"):
 * - a faixa de abas de um espaço: a aba entra nele, na posição; na do próprio espaço, muda de lugar;
 * - o miolo de outro espaço (ou ele recolhido): a aba entra no fim;
 * - perto de uma borda do conteúdo de um espaço: ele se divide, e a aba fica num espaço novo
 *   daquele lado (o próprio espaço só se divide se outra aba fica nele);
 * - a borda da janela e o centro vazio: um espaço novo ali.
 */
function tabDropAt(x: number, y: number, sheetId: string, from: ContentNode, win: string | null): Drop | null {
  for (const el of document.elementsFromPoint(x, y)) {
    if (!(el instanceof HTMLElement)) continue;
    const r = el.getBoundingClientRect();
    const side = el.dataset.dropEdge as DropSide | undefined;
    if (side) return edgeDrop(side, el, win);
    if (el.dataset.dropCenter !== undefined) return { target: { kind: 'center' }, rect: boxOf(r) };
    const strip = el.dataset.tabsOf;
    if (strip) return stripDrop(el, strip, x, sheetId, strip === from.id);
    const nodeId = el.dataset.node;
    if (!nodeId) continue;
    const own = nodeId === from.id;
    const zone = bodyZone(el, x, y);
    if (zone && (!own || from.sheets.length > 1)) return { target: { kind: 'split', nodeId, side: zone }, rect: zoneRect(boxOf(r), zone) };
    return own ? null : { target: { kind: 'into', nodeId }, before: null, rect: boxOf(r) };
  }
  return null;
}

/** Onde o "+" põe: um lugar da janela (o modo de edição) ou dentro de um espaço (o "+" da faixa de abas). */
type AddTarget = DropTarget | { kind: 'into'; nodeId: string };

/**
 * Os arrastes e o "+": a qualquer hora, arrastar uma aba, ou o espaço inteiro pela parte vazia da
 * faixa, e o "+" da faixa de abas; no modo de edição, o "+" que põe uma aba num lugar.
 */
type EditApi = {
  editing: boolean;
  /** `sheet`: a aba à vista, a que se duplica com Ctrl ou Alt (o espaço vazio não tem) */
  startDrag: (node: ContentNode, sheet: Sheet | null, e: RPointerEvent) => void;
  startTabDrag: (node: ContentNode, sheet: Sheet, e: RPointerEvent) => void;
  openAdd: (target: AddTarget, anchor: MenuAnchor) => void;
};
const EditCtx = createContext<EditApi>({ editing: false, startDrag: () => undefined, startTabDrag: () => undefined, openAdd: () => undefined });

/** O clique que vem com o soltar de um arraste não põe à vista a aba que estava embaixo. */
function swallowClick() {
  const swallow = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

/** Abre numa janela própria nova uma aba, ou um espaço inteiro (o elemento dá o tamanho e o lugar da janela). */
type PopApi = { sheet: (sheet: Sheet, el?: Element | null) => void; panel: (node: ContentNode, el?: Element | null) => void };

/**
 * A janela que desenha os espaços: o layout guardado (os menus acham nele os espaços de todas as
 * janelas), o que ela desenha (`shown`), a janela própria (null na mesa), como ela muda o layout e
 * como leva uma aba, ou um espaço, para uma janela nova.
 */
type DeskApi = { layout: DesktopLayout; shown: DesktopLayout; win: string | null; apply: (op: LayoutOp) => void; popOut: PopApi };
const DeskCtx = createContext<DeskApi | null>(null);
const useDesk = (): DeskApi => useContext(DeskCtx)!;

type MaxBox = { left: number; top: number; width: number; height: number };
/** Espaço ampliado (id do nó) e a área do corpo da janela que ele passa a ocupar. */
const MaxCtx = createContext<{ maxNode: string | null; box: MaxBox | null; toggle: (id: string) => void }>({ maxNode: null, box: null, toggle: () => undefined });

/**
 * As abas que os menus abrem (o "+" do modo de edição, o da faixa de abas, o menu Abas): o chat da
 * mesa, os módulos e as configurações; e as tabelas da sala, à parte, com o nome de cada uma.
 */
export function useTabChoices() {
  const t = useT();
  // sem o registro das sessões, a aba Sessões sai dos menus (docs/spec/session-clock.md, "Ligar e desligar")
  const record = useApp((s) => sessionRules(s.room).record);
  const dockKey = useApp((s) =>
    s.library
      .filter((i) => i.kind === 'dock' && !i.inactive)
      .map((i) => i.id)
      .join(','),
  );
  const tabChoices: { classId: ModuleClassId; label: string }[] = [
    { classId: 'system/mainChat', label: t(MODULES['system/mainChat'].titleKey) },
    ...Object.values(MODULES)
      .filter((m) => m.addable && (record || m.classId !== 'system/gameSessions'))
      .map((m) => ({ classId: m.classId, label: t(m.titleKey) })),
    { classId: 'system/roomSettings', label: t(MODULES['system/roomSettings'].titleKey) },
  ];
  const tableLabel = (itemId: string) => {
    const { library, plugins } = useApp.getState();
    const item = library.find((i) => i.id === itemId);
    return item ? tableName(item, t, plugins) : t('module.tableDock');
  };
  const iconOf = (classId: ModuleClassId) => ({ icon: <Icon name={MODULES[classId].icon} size={16} /> });
  return { tabChoices, tableIds: dockKey ? dockKey.split(',') : [], tableLabel, iconOf };
}

export type DeskProps = {
  /** o layout guardado, com todas as janelas */
  layout: DesktopLayout;
  /** o que esta janela desenha: a mesa sem as janelas próprias, ou o pedaço de uma janela (no centro) */
  shown: DesktopLayout;
  /** a janela própria que desenha; null na mesa */
  win: string | null;
  roomId: string;
  /** muda o layout (na janela própria, o pedido vai para a principal) */
  apply: (op: LayoutOp) => void;
  /** o modo de edição (Ctrl+E) desta janela */
  editing: boolean;
  onEditing: (editing: boolean) => void;
  /** a pessoa usou um espaço: clicou nele, ou ele recebeu uma aba arrastada */
  onTouch: (nodeId: string) => void;
  /** "Voltar ao arranjo inicial", no modo de edição (só na mesa) */
  onReset?: () => void;
  className?: string;
  /** o que vai junto, dentro da mesa: o aviso de reconexão, o áudio, a voz, os diálogos */
  children?: ReactNode;
};

/** A mesa, ou a janela própria: os lugares com os espaços, o modo de edição, os arrastes e os menus do "+". */
export function Desk({ layout, shown, win, roomId, apply, editing, onEditing, onTouch, onReset, className, children }: DeskProps) {
  const t = useT();
  const { tabChoices, tableIds, tableLabel, iconOf } = useTabChoices();

  // abrir uma janela nova com a aba, ou com o espaço inteiro: a janela abre dentro do clique (senão o
  // navegador bloqueia), do tamanho e no lugar do espaço, e a aba (ou o espaço) vai para ela
  const popOut: PopApi = {
    sheet: (sheet, el) => {
      const id = newLayoutId('w');
      if (!openPopout(id, roomId, el?.getBoundingClientRect() ?? null)) toast(t('room.popout.blocked'), 'error');
      else apply({ op: 'popTab', sheetId: sheet.id, win: id });
    },
    panel: (node, el) => {
      const id = newLayoutId('w');
      if (!openPopout(id, roomId, el?.getBoundingClientRect() ?? null)) toast(t('room.popout.blocked'), 'error');
      else apply({ op: 'popPanel', nodeId: node.id, win: id });
    },
  };

  // espaço ampliado: cobre o corpo da janela (como maximizar a janela da ficha no original)
  const [maxNode, setMaxNode] = useState<string | null>(null);
  const [bodyBox, setBodyBox] = useState<MaxBox | null>(null);

  // largura do corpo (decide laterais encolhidas e o acordeão); o ref de callback pega o elemento
  // quando ele monta
  const [bodyEl, bodyRef] = useState<HTMLDivElement | null>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  // a largura de dentro, sem a margem do corpo: a que as colunas e o centro dividem
  const [bodyInner, setBodyInner] = useState(0);
  useEffect(() => {
    if (!bodyEl) return;
    const measure = () => {
      setBodyWidth(bodyEl.clientWidth);
      const style = getComputedStyle(bodyEl);
      setBodyInner(bodyEl.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0));
      const r = bodyEl.getBoundingClientRect();
      setBodyBox({ left: r.left, top: r.top, width: r.width, height: r.height });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(bodyEl);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [bodyEl]);
  // Esc devolve o espaço ampliado ao lugar
  useEffect(() => {
    if (!maxNode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role=dialog]')) setMaxNode(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maxNode]);

  // Modo de edição do layout (docs/spec/room-layout.md): Ctrl+E (Cmd+E no Mac) liga e desliga,
  // de qualquer lugar da janela; Esc sai. Entrar e sair devolvem o espaço ampliado ao lugar.
  const toggleEditing = useCallback(() => onEditing(!editing), [onEditing, editing]);
  const [seenEditing, setSeenEditing] = useState(editing);
  if (seenEditing !== editing) {
    setSeenEditing(editing);
    setMaxNode(null);
  }
  const dragging = useRef(false);
  useEffect(() => {
    if (!bodyEl) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.repeat) return;
      if (e.code !== 'KeyE' && e.key.toLowerCase() !== 'e') return;
      // a mesa continua montada (escondida) enquanto a pessoa está noutra tela
      if (bodyEl.closest('[hidden]')) return;
      // o navegador usaria o atalho para a busca na barra de endereço
      e.preventDefault();
      toggleEditing();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [bodyEl, toggleEditing]);
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || dragging.current || document.querySelector('[role=dialog], [role=menu]')) return;
      onEditing(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, onEditing]);

  // o último espaço usado (docs/spec/room-layout.md, "Para onde vai uma aba nova")
  const touchPanel = (e: RPointerEvent) => {
    const id = (e.target as Element | null)?.closest?.('[data-node]')?.getAttribute('data-node');
    if (id) onTouch(id);
  };

  // arrastar o espaço inteiro pela parte vazia da faixa, a qualquer hora (docs/spec/room-layout.md,
  // "Mover um espaço inteiro"): o destaque mostra onde ele cai; soltar leva o espaço para lá, ou
  // junta as abas dele às de outro (com Ctrl ou Alt apertado, vai uma cópia da aba à vista), e Esc
  // desiste. O dedo não arrasta: segurar abre o menu do espaço, que tem o "Mover para".
  const [drag, setDrag] = useState<{ node: ContentNode; sheet: Sheet | null; x: number; y: number; drop: Drop | null; copy: boolean } | null>(null);
  const startDrag = useCallback(
    (node: ContentNode, sheet: Sheet | null, e: RPointerEvent) => {
      if (e.button !== 0 || e.pointerType === 'touch') return;
      const handle = e.currentTarget as HTMLElement;
      const pointerId = e.pointerId;
      const start = { x: e.clientX, y: e.clientY };
      let started = false;
      const move = (ev: PointerEvent) => {
        if (!started) {
          if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_START) return;
          started = true;
          dragging.current = true;
          // os eventos seguem para a faixa mesmo por cima das fichas (iframes)
          try {
            handle.setPointerCapture(pointerId);
          } catch {
            // o espaço já saiu da página
          }
        }
        setDrag({ node, sheet, x: ev.clientX, y: ev.clientY, drop: dropAt(ev.clientX, ev.clientY, node, copying(ev), win), copy: copying(ev) });
      };
      const copying = (ev: PointerEvent) => !!sheet && CLONABLE.has(sheet.classId) && (ev.ctrlKey || ev.altKey || ev.metaKey);
      const finish = (ev: PointerEvent | null) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('keydown', key, true);
        const drop = started && ev ? dropAt(ev.clientX, ev.clientY, node, copying(ev), win) : null;
        if (drop && ev) {
          const copy = copying(ev) ? sheet : null;
          apply(copy ? { op: 'cloneSheet', sheetId: copy.id, to: drop.target, before: drop.before } : { op: 'movePanel', nodeId: node.id, to: drop.target, before: drop.before });
          // o espaço que recebeu as abas é o último usado
          if (drop.target.kind === 'into') onTouch(drop.target.nodeId);
        }
        if (started) swallowClick();
        dragging.current = false;
        setDrag(null);
      };
      const up = (ev: PointerEvent) => finish(ev);
      const cancel = () => finish(null);
      const key = (ev: KeyboardEvent) => {
        if (ev.key !== 'Escape' || !started) return;
        // só desiste do arraste: o modo de edição continua
        ev.preventDefault();
        ev.stopPropagation();
        finish(null);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
      window.addEventListener('keydown', key, true);
    },
    [apply, onTouch, win],
  );

  // arrastar uma aba, a qualquer hora (docs/spec/room-layout.md, "Arrastar as abas"): o destaque
  // mostra onde ela cai; soltar a leva para lá (com Ctrl ou Alt apertado, uma cópia, no que pode
  // ter cópia), Esc desiste. O dedo não arrasta: a faixa rola de lado, e segurar abre o menu da aba.
  const [tabDrag, setTabDrag] = useState<{ sheet: Sheet; x: number; y: number; drop: Drop | null; copy: boolean } | null>(null);
  const startTabDrag = useCallback(
    (node: ContentNode, sheet: Sheet, e: RPointerEvent) => {
      if (e.button !== 0 || e.pointerType === 'touch' || (e.target as HTMLElement).closest('.rd-dock__tabClose')) return;
      const handle = e.currentTarget as HTMLElement;
      const pointerId = e.pointerId;
      const start = { x: e.clientX, y: e.clientY };
      let started = false;
      const copying = (ev: PointerEvent) => CLONABLE.has(sheet.classId) && (ev.ctrlKey || ev.altKey || ev.metaKey);
      const move = (ev: PointerEvent) => {
        if (!started) {
          if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_START) return;
          started = true;
          dragging.current = true;
          // os eventos seguem para a aba mesmo por cima das fichas (iframes)
          try {
            handle.setPointerCapture(pointerId);
          } catch {
            // a aba já saiu da página
          }
        }
        setTabDrag({ sheet, x: ev.clientX, y: ev.clientY, drop: tabDropAt(ev.clientX, ev.clientY, sheet.id, node, win), copy: copying(ev) });
      };
      const finish = (ev: PointerEvent | null) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('keydown', key, true);
        const drop = started && ev ? tabDropAt(ev.clientX, ev.clientY, sheet.id, node, win) : null;
        if (drop && ev) {
          const op = copying(ev) ? 'cloneSheet' : 'moveSheet';
          apply({ op, sheetId: sheet.id, to: drop.target, before: drop.before });
          // o espaço que recebeu a aba é o último usado
          if (drop.target.kind === 'into') onTouch(drop.target.nodeId);
        }
        if (started) swallowClick();
        dragging.current = false;
        setTabDrag(null);
      };
      const up = (ev: PointerEvent) => finish(ev);
      const cancel = () => finish(null);
      const key = (ev: KeyboardEvent) => {
        if (ev.key !== 'Escape' || !started) return;
        // só desiste do arraste (e do modo de edição, se estiver nele, não sai)
        ev.preventDefault();
        ev.stopPropagation();
        finish(null);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
      window.addEventListener('keydown', key, true);
    },
    [apply, onTouch, win],
  );

  // o "+": no modo de edição, o menu do que pode ir para aquele lugar; na faixa de abas, o das
  // abas que abrem naquele espaço
  const [addMenu, setAddMenu] = useState<{ target: AddTarget; anchor: MenuAnchor } | null>(null);
  const editApi: EditApi = { editing, startDrag, startTabDrag, openAdd: (target, anchor) => setAddMenu({ target, anchor }) };

  // o "+" do modo de edição: um espaço com a aba, no lugar; a aba que já está na mesa muda de lugar
  const placeEntries = (target: DropTarget): MenuEntry[] => [
    { kind: 'title', label: t('room.layout.add.title') },
    ...tabChoices.map((c) => ({ label: c.label, ...iconOf(c.classId), onSelect: () => apply({ op: 'place', classId: c.classId, to: target }) })),
    ...(tableIds.length ? [{ kind: 'separator' as const }] : []),
    ...tableIds.map((id) => ({ label: tableLabel(id), ...iconOf('system/tableDock'), onSelect: () => apply({ op: 'place', classId: 'system/tableDock', param: id, to: target }) })),
  ];
  // o "+" da faixa de abas: a aba abre naquele espaço; a que já está nele tem ✓, e a que está em
  // outro espaço vem para cá. Numa janela própria, o chat da mesa fica onde está, e vem uma cópia
  const intoEntries = (nodeId: string): MenuEntry[] => {
    const space = findContent(layout, nodeId);
    const entry = (classId: ModuleClassId, label: string, param?: string): MenuEntry => {
      const found = findTab(layout, classId, param);
      const copy = !!win && classId === 'system/mainChat';
      const here = copy ? !!space?.sheets.some((s) => s.classId === classId) : found?.nodeId === nodeId;
      return {
        label,
        ...iconOf(classId),
        ...(here ? { checked: true, toggle: true } : copy ? { hint: t('room.tab.add.chatCopy') } : found ? { hint: t('room.tab.add.elsewhere') } : {}),
        onSelect: () => apply({ op: 'openInto', classId, nodeId, ...(param ? { param } : {}) }),
      };
    };
    return [
      { kind: 'title', label: t('room.tab.add') },
      ...tabChoices.map((c) => entry(c.classId, c.label)),
      ...(tableIds.length ? [{ kind: 'separator' as const }] : []),
      ...tableIds.map((id) => entry('system/tableDock', tableLabel(id), id)),
    ];
  };
  const addEntries: MenuEntry[] = !addMenu ? [] : addMenu.target.kind === 'into' ? intoEntries(addMenu.target.nodeId) : placeEntries(addMenu.target);

  const site = (place: DockPlace) => shown.sites[place];
  // laterais encolhem (sem gravar) para o centro caber; abaixo de ACCORDION_BELOW, acordeão (numa
  // janela própria, só com mais de um espaço: sozinho, ele já mostra as abas todas)
  const sidePlaces: DockPlace[] = ['mostLeft', 'left', 'right', 'mostRight'];
  const sideTotal = sidePlaces.reduce((sum, p) => sum + (site(p)?.size ?? 0), 0);
  const sideCount = sidePlaces.filter((p) => site(p)).length;
  const centerMin = Math.max(CENTER_MIN, site('client') ? minWidthOf(site('client')!.node) : 0);
  const room4Sides = Math.max(0, bodyInner - centerMin - sideCount * SPLITTER);
  const sideFactor = bodyWidth > 0 && sideTotal > room4Sides ? room4Sides / sideTotal : 1;
  const accordion = bodyWidth > 0 && bodyWidth < ACCORDION_BELOW && (!win || listPanels(shown).length > 1);
  const maxCtx = { maxNode: accordion ? null : maxNode, box: bodyBox, toggle: (id: string) => setMaxNode((m) => (m === id ? null : id)) };

  /** A largura de uma coluna na tela: encolhida, se o centro não caberia. */
  const shownWidth = (size: number) => (sideFactor < 1 ? Math.max(SIDE_MIN, Math.round(size * sideFactor)) : size);
  /**
   * Até onde o divisor de uma coluna vai, em px na tela: do mínimo dos espaços lado a lado nela
   * (sozinho, o da coluna) até a largura que sobra com o centro no mínimo e as outras colunas como estão
   */
  const columnLimits = (place: DockPlace, node: DockNode) => {
    const others = sidePlaces.reduce((sum, p) => (p !== place && site(p) ? sum + shownWidth(site(p)!.size) : sum), 0);
    const min = Math.max(SITE_MIN, node.kind === 'group' ? minWidthOf(node) : 0);
    return { min, max: Math.max(min, room4Sides - others) };
  };

  const renderSite = (place: DockPlace, edge: 'left' | 'right' | 'top' | 'bottom') => {
    const s = site(place);
    if (!s) return null;
    const horizontal = edge === 'left' || edge === 'right';
    const size = horizontal ? shownWidth(s.size) : s.size;
    // o espaço sozinho na lateral, recolhido: a coluna vira uma tira (a faixa de abas, de pé, ou
    // deitada na faixa de cima ou de baixo); o espaço vazio não recolhe
    const fit = s.node.kind === 'content' && !!s.node.collapsed && s.node.sheets.length > 0 && maxCtx.maxNode !== s.node.id;
    return (
      <SideSite
        key={place}
        place={place}
        size={size}
        stored={s.size}
        factor={horizontal ? sideFactor : 1}
        limits={horizontal ? columnLimits(place, s.node) : null}
        edge={edge}
        fit={fit}
        onResize={(next) => apply({ op: 'resizeSite', place, size: next })}
      >
        <NodeView node={s.node} collapseAs={horizontal ? 'strip' : 'bar'} />
      </SideSite>
    );
  };

  // as bordas recebem a aba e o espaço arrastados (fora do modo de edição, por cima dos espaços, sem
  // empurrar nada) e têm o "+" do modo de edição
  const edges = editing || !!tabDrag || !!drag;
  return (
    <div className={`rd-room${className ? ` ${className}` : ''}${drag || tabDrag ? ' rd-room--dragging' : ''}`}>
      {children}
      {editing ? (
        <div className="rd-layoutEdit" role="region" aria-label={t('room.layout.edit')}>
          <span className="rd-layoutEdit__text">{accordion ? t('room.layout.banner.narrow') : t(win ? 'room.layout.banner.window' : 'room.layout.banner')}</span>
          {onReset ? (
            <button type="button" className="rd-btn rd-btn--secondary" onClick={onReset}>
              {t('room.layout.reset')}
            </button>
          ) : null}
          <button type="button" className="rd-btn rd-btn--action" onClick={toggleEditing}>
            {t('room.layout.done')}
          </button>
        </div>
      ) : null}

      <DeskCtx.Provider value={{ layout, shown, win, apply, popOut }}>
      <MaxCtx.Provider value={maxCtx}>
      <EditCtx.Provider value={accordion ? { ...editApi, editing: false } : editApi}>
      <div
        ref={bodyRef}
        className={`rd-room__body${editing ? ' rd-room__body--editing' : ''}${accordion ? ' rd-room__body--accordion' : ''}`}
        onPointerDownCapture={touchPanel}
      >
        {accordion ? (
          <AccordionView layout={shown} />
        ) : (
          <>
            {edges ? <EdgeZone side="left" /> : null}
            {renderSite('mostLeft', 'right')}
            {renderSite('left', 'right')}
            <div className="rd-room__center">
              {edges ? <EdgeZone side="top" /> : null}
              {renderSite('top', 'bottom')}
              {site('client') ? (
                <div className="rd-room__client">
                  <NodeView node={site('client')!.node} collapseAs={null} />
                </div>
              ) : (
                <EmptyCenter />
              )}
              {renderSite('bottom', 'top')}
              {edges ? <EdgeZone side="bottom" /> : null}
            </div>
            {renderSite('right', 'left')}
            {renderSite('mostRight', 'left')}
            {edges ? <EdgeZone side="right" /> : null}
          </>
        )}
      </div>
      </EditCtx.Provider>
      </MaxCtx.Provider>
      </DeskCtx.Provider>
      {addMenu ? (
        <ContextMenu anchor={addMenu.anchor} entries={addEntries} label={t(addMenu.target.kind === 'into' ? 'room.tab.add' : 'room.layout.add.title')} onClose={() => setAddMenu(null)} />
      ) : null}
      {drag ? (
        <>
          {drag.drop?.rect ? <div className="rd-layoutEdit__drop" style={drag.drop.rect} aria-hidden="true" /> : null}
          {drag.drop?.marker ? <div className="rd-tabDrop__marker" style={drag.drop.marker} aria-hidden="true" /> : null}
          {/* o espaço se chama pela aba à vista; "+2": as outras abas que vão junto */}
          <div className={`rd-layoutEdit__ghost${drag.copy ? ' is-copy' : ''}`} style={{ left: drag.x + 14, top: drag.y + 14 }} aria-hidden="true">
            {drag.copy ? <Icon name="plus" size={12} /> : null}
            {drag.sheet ? <SheetTitle sheet={drag.sheet} /> : t('room.panel.emptyLabel')}
            {!drag.copy && drag.node.sheets.length > 1 ? <span className="rd-layoutEdit__ghostMore">+{drag.node.sheets.length - 1}</span> : null}
          </div>
        </>
      ) : null}
      {tabDrag ? (
        <>
          {tabDrag.drop?.rect ? <div className="rd-layoutEdit__drop" style={tabDrag.drop.rect} aria-hidden="true" /> : null}
          {tabDrag.drop?.marker ? <div className="rd-tabDrop__marker" style={tabDrag.drop.marker} aria-hidden="true" /> : null}
          <div className={`rd-layoutEdit__ghost${tabDrag.copy ? ' is-copy' : ''}`} style={{ left: tabDrag.x + 14, top: tabDrag.y + 14 }} aria-hidden="true">
            {tabDrag.copy ? <Icon name="plus" size={12} /> : null}
            <SheetTitle sheet={tabDrag.sheet} />
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Os menus de um espaço (docs/spec/room-menus.md):
 * - o do espaço, pelo "Mais ações" (⋯) ou pelo botão direito na parte vazia da faixa: abrir em
 *   nova janela (o espaço inteiro, com as abas), ampliar (ou restaurar), mover o espaço para um
 *   lado da mesa (de uma janela própria, de volta para a mesa) e fechar o espaço;
 * - o de uma aba (na faixa e no acordeão), pelo botão direito ou pelo dedo segurando: abrir em nova
 *   janela, mover para junto de outro espaço, desta ou de outra janela (ou para um novo), duplicar e
 *   fechar.
 * Numa janela própria, o único espaço dela (e a única aba dele) não vai para outra janela nova: já
 * está sozinho numa. Devolve os handlers da aba e da parte vazia da faixa, o que abre o menu pelo
 * botão e o menu a desenhar.
 */
function usePanelMenus() {
  const t = useT();
  const { layout, shown, win, apply, popOut } = useDesk();
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; sheet: Sheet | null; el: Element | null; nodeId?: string; byButton?: true } | null>(null);
  const press = useLongPress();
  const tabHandlers = (sheet: Sheet, el: () => Element | null) => press.bind((anchor) => setMenu({ anchor, sheet, el: el() }));
  const spaceHandlers = (sheet: Sheet | null, el: () => Element | null, nodeId: string) => press.bind((anchor) => setMenu({ anchor, sheet, el: el(), nodeId }));
  // pelo botão "Mais ações" do espaço
  const open = (sheet: Sheet | null, anchor: MenuAnchor, el: Element | null, nodeId: string) => setMenu({ anchor, sheet, el, nodeId, byButton: true });
  const nodeId = menu?.nodeId;
  const sheet = menu?.sheet ?? null;
  const side = nodeId && !win ? sideOf(layout, nodeId) : null;
  const panel = nodeId ? findContent(layout, nodeId) : null;
  const mainPanel = !!panel?.sheets.some((s) => s.main);
  const max = useContext(MaxCtx);
  const isMax = !!nodeId && max.maxNode === nodeId && !!max.box;
  const clonable = !!sheet && CLONABLE.has(sheet.classId);
  const alone = !!win && listPanels(shown).length === 1;
  // o menu da aba: o espaço dela e os outros, de todas as janelas, para onde ela pode ir (o chat da
  // mesa não vai para uma janela própria); os desta janela primeiro
  const home = sheet ? sheetPanel(layout, sheet.id) : null;
  const others = home
    ? spacesByDesk(layout)
        .filter((x) => x.node.id !== home.id && !(sheet?.main && x.win))
        .sort((a, b) => Number(a.win !== win) - Number(b.win !== win))
    : [];
  // um espaço nos menus: pela aba à vista ("Junto de Masmorra"), ou "Espaço vazio"
  const spaceText = (n: ContentNode) => {
    const shownTab = n.sheets.find((s) => s.id === n.active) ?? n.sheets[0];
    return shownTab ? t('room.tab.moveInto', { name: sheetText(shownTab, t) }) : t('room.panel.emptyLabel');
  };
  // onde ele está: o lado da mesa; numa janela própria, nesta janela, na mesa ou noutra janela
  const whereOf = (x: { node: ContentNode; win: string | null }): string | undefined => {
    if (x.win === win) {
      const at = win ? null : sideOf(layout, x.node.id);
      return win ? t('room.where.here') : at ? t(`room.move.${at}`) : undefined;
    }
    return t(x.win ? 'room.where.otherWindow' : 'room.where.table');
  };
  const panelEntries = (node: ContentNode, nodeId: string): MenuEntry[] => [
    // o espaço inteiro, com as abas, e ampliar; o vazio não tem o que levar nem o que mostrar
    ...(node.sheets.length && !alone ? [{ label: t('room.popout'), onSelect: () => popOut.panel(node, menu?.el) }] : []),
    // ampliado, o espaço cobre a janela; Esc também restaura
    ...(!node.sheets.length
      ? []
      : isMax
        ? [{ label: t('room.panel.restore'), shortcut: 'Esc', onSelect: () => max.toggle(nodeId) }]
        : [{ label: t('room.panel.maximize'), onSelect: () => max.toggle(nodeId) }]),
    // para um lado da mesa (arrastar a parte vazia da faixa leva a qualquer lugar); de uma janela
    // própria, de volta para a mesa
    {
      kind: 'submenu' as const,
      label: t(win ? 'room.move.table' : 'room.move'),
      entries: (['left', 'center', 'right'] as const).map((to) => ({
        label: t(`room.move.${to}`),
        ...(win ? {} : { checked: side === to }),
        onSelect: () => apply({ op: 'movePanelTo', nodeId, side: to }),
      })),
    },
    // as abas e o espaço, juntos; o do chat da mesa não fecha (o último de uma janela a fecha junto)
    ...(mainPanel ? [] : [{ kind: 'separator' as const }, { label: t('room.closePanel'), onSelect: () => apply({ op: 'closePanel', nodeId }) }]),
  ];
  const tabEntries = (tab: Sheet): MenuEntry[] => [
    ...(alone && home?.sheets.length === 1 ? [] : [{ label: t('room.popout'), onSelect: () => popOut.sheet(tab, menu?.el) }]),
    // para junto de outro espaço, pela aba à vista dele (com o lugar como dica), ou para um espaço
    // novo ao lado
    ...(others.length || (home && home.sheets.length > 1)
      ? [
          {
            kind: 'submenu' as const,
            label: t('room.tab.moveTo'),
            entries: [
              ...others.map((x) => {
                const hint = whereOf(x);
                return { label: spaceText(x.node), ...(hint ? { hint } : {}), onSelect: () => apply({ op: 'moveSheet', sheetId: tab.id, to: { kind: 'into', nodeId: x.node.id } }) };
              }),
              ...(home && home.sheets.length > 1
                ? [
                    ...(others.length ? [{ kind: 'separator' as const }] : []),
                    { label: t('room.panel.new'), onSelect: () => apply({ op: 'moveSheet', sheetId: tab.id, to: { kind: 'split', nodeId: home.id, side: besideSide(layout, home.id) } }) },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(clonable && home ? [{ label: t('room.layout.clone'), onSelect: () => apply({ op: 'cloneSheet', sheetId: tab.id, to: { kind: 'split', nodeId: home.id, side: besideSide(layout, home.id) } }) }] : []),
    ...(tab.main ? [] : [{ label: t('room.closeModule'), onSelect: () => apply({ op: 'closeSheet', sheetId: tab.id }) }]),
  ];
  const entries: MenuEntry[] = !menu ? [] : nodeId && panel ? panelEntries(panel, nodeId) : sheet ? tabEntries(sheet) : [];
  const view = menu ? <ContextMenu anchor={menu.anchor} entries={entries} label={t(nodeId ? 'room.panelMenu' : 'room.modules')} onClose={() => setMenu(null)} /> : null;
  return { tabHandlers, spaceHandlers, open, openByButton: !!menu?.byButton, view };
}

/**
 * Um botão que abre um menu: com o menu dele aberto, clicar de novo fecha. O menu fecha no
 * mousedown fora dele (o botão também é fora), e o clique que vem em seguida não o reabre.
 */
function useMenuToggle(openKey: string | null) {
  const closing = useRef<string | null>(null);
  return {
    onMouseDown: (key: string) => {
      closing.current = openKey === key ? key : null;
    },
    /** o clique abre o menu? */
    opens: (key: string) => {
      const skip = closing.current === key;
      closing.current = null;
      return !skip;
    },
  };
}

function PopoutButton({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button type="button" className="rd-dock__max" aria-label={t('room.popout')} title={t('room.popout')} onClick={onClick}>
      <Icon name="popout" size={13} />
    </button>
  );
}

/**
 * Janela estreita (o "acordeão" do original): um módulo por vez, com as abas de todos em cima, e o
 * "+", que abre uma aba no espaço da que está à vista. Abas novas (ficha aberta, conversa privada)
 * ficam visíveis. Os espaços vazios não aparecem.
 */
function AccordionView({ layout }: { layout: DesktopLayout }) {
  const t = useT();
  const { apply, popOut, win } = useDesk();
  const edit = useContext(EditCtx);
  const tabMenu = usePanelMenus();
  const bodyRef = useRef<HTMLDivElement>(null);
  const sheets = listSheets(layout);
  const [active, setActive] = useState<string | null>(null);
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set(sheets.map((s) => s.id));
    if (known.current) {
      const fresh = sheets.find((s) => !known.current!.has(s.id));
      if (fresh) setActive(fresh.id);
    }
    known.current = ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets.map((s) => s.id).join(',')]);
  const current = sheets.find((s) => s.id === active) ?? sheets.find((s) => s.main) ?? sheets[0];
  if (!current) return null;
  const home = sheetPanel(layout, current.id);
  return (
    <div className="rd-accordion">
      <div className="rd-accordion__tabs">
        <TabList active={current.id} label={t('room.modules')}>
          {sheets.map((s) => (
            <div key={s.id} className={`rd-dock__tab${s.id === current.id ? ' rd-dock__tab--active' : ''}`} {...tabMenu.tabHandlers(s, () => bodyRef.current)}>
              <button type="button" role="tab" aria-selected={s.id === current.id} className="rd-dock__tabBtn" onPointerEnter={fullTitle} onClick={() => setActive(s.id)}>
                <TabLabel sheet={s} />
              </button>
              {!s.main ? (
                <button type="button" className="rd-dock__tabClose" aria-label={t('room.closeModule')} onClick={() => apply({ op: 'closeSheet', sheetId: s.id })}>
                  <Icon name="close" size={12} />
                </button>
              ) : null}
            </div>
          ))}
        </TabList>
        {home ? (
          <button
            type="button"
            className="rd-dock__tabAdd"
            aria-label={t('room.tab.add')}
            title={t('room.tab.add')}
            aria-haspopup="menu"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              edit.openAdd({ kind: 'into', nodeId: home.id }, { x: r.left, y: r.bottom + 2 });
            }}
          >
            <Icon name="plus" size={13} />
          </button>
        ) : null}
        {/* o relógio da sessão fica na janela da mesa, com o chat da mesa (docs/spec/session-clock.md) */}
        {win ? null : <ClockPill />}
        {/* numa janela própria, a única aba dela já está sozinha numa janela */}
        {win && sheets.length === 1 ? null : <PopoutButton onClick={() => popOut.sheet(current, bodyRef.current)} />}
      </div>
      {tabMenu.view}
      <div ref={bodyRef} className="rd-dock__card rd-accordion__body">
        <div className="rd-dock__body" role="tabpanel">
          {MODULES[current.classId].render(current)}
        </div>
      </div>
    </div>
  );
}

/** Puxador no vão entre cartões: arrastar redimensiona. Enquanto arrasta, a linha fica acesa. */
function Splitter({ horizontal, onDrag }: { horizontal: boolean; onDrag: (delta: number) => void; }) {
  const start = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const onDown = (e: RPointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    start.current = horizontal ? e.clientX : e.clientY;
    setDragging(true);
  };
  const onMove = (e: RPointerEvent) => {
    if (start.current === null) return;
    const pos = horizontal ? e.clientX : e.clientY;
    onDrag(pos - start.current);
    start.current = pos;
  };
  const stop = () => {
    start.current = null;
    setDragging(false);
  };
  return (
    <div
      className={`rd-room__splitter rd-room__splitter--${horizontal ? 'v' : 'h'}${dragging ? ' is-dragging' : ''}`}
      role="separator"
      aria-orientation={horizontal ? 'vertical' : 'horizontal'}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
    >
      <span className="rd-room__grip" />
    </div>
  );
}

function SideSite({
  place,
  size,
  stored,
  factor,
  limits,
  edge,
  fit,
  onResize,
  children,
}: {
  place: DockPlace;
  /** o tamanho na tela (encolhido, se o centro não caberia) */
  size: number;
  /** o tamanho gravado no layout, e quanto dele aparece (`factor`) */
  stored: number;
  factor: number;
  /** de quanto a quanto a coluna vai, na tela (a faixa de cima ou de baixo não tem: vale o teto do layout) */
  limits: { min: number; max: number } | null;
  edge: 'left' | 'right' | 'top' | 'bottom';
  /** o espaço recolhido sozinho no lugar: o lugar fica do tamanho dele, sem puxador */
  fit: boolean;
  onResize: (size: number) => void;
  children: React.ReactNode;
}) {
  const horizontal = edge === 'left' || edge === 'right';
  const storedRef = useRef({ stored, factor, limits });
  storedRef.current = { stored, factor, limits };
  const sign = edge === 'right' || edge === 'bottom' ? 1 : -1;
  // o arraste fica dentro dos limites a cada passo: passar do fim e voltar mexe na hora, sem ponto morto
  const drag = (d: number) => {
    const { stored: now, factor: shown, limits: range } = storedRef.current;
    const next = dragSiteSize(now, sign * d, shown);
    onResize(range ? Math.min(range.max / shown, Math.max(range.min / shown, next)) : next);
  };
  const splitter = fit ? <div className={`rd-room__gap rd-room__gap--${horizontal ? 'v' : 'h'}`} /> : <Splitter horizontal={horizontal} onDrag={drag} />;
  return (
    <>
      {edge === 'left' || edge === 'top' ? splitter : null}
      <div className={`rd-room__site rd-room__site--${place}`} style={fit ? undefined : horizontal ? { width: size } : { height: size }}>
        {children}
      </div>
      {edge === 'right' || edge === 'bottom' ? splitter : null}
    </>
  );
}

/**
 * Como o espaço recolhe: só a faixa de abas, deitada (`bar`: empilhado com outros, ou numa faixa de
 * cima ou de baixo), ou de pé, uma tira com as abas deitadas (`strip`: lado a lado com outros, ou
 * sozinho numa coluna). Sozinho no centro, não recolhe.
 */
type CollapseAs = 'bar' | 'strip' | null;

function NodeView({ node, collapseAs }: { node: DockNode; collapseAs: CollapseAs }) {
  const max = useContext(MaxCtx);
  const edit = useContext(EditCtx);
  const { apply, win } = useDesk();
  const menus = usePanelMenus();
  const moreToggle = useMenuToggle(menus.openByButton ? 'more' : null);
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  if (node.kind === 'group') {
    const horizontal = node.orientation === 'horizontal';
    const total = node.sizes.reduce((a, b) => a + b, 0) || 1;
    return (
      <div ref={ref} className={`rd-dock__group rd-dock__group--${node.orientation}`}>
        {node.children.map((c, i) => (
          <GroupCell key={c.id} grow={node.sizes[i] ?? 1} minWidth={horizontal ? minWidthOf(c) : 0} folded={c.kind === 'content' && !!c.collapsed && c.sheets.length > 0 && max.maxNode !== c.id}>
            {i > 0 ? (
              <Splitter
                horizontal={horizontal}
                onDrag={(d) => {
                  const box = ref.current?.getBoundingClientRect();
                  const span = box ? (horizontal ? box.width : box.height) : 1000;
                  apply({ op: 'sizes', groupId: node.id, index: i - 1, delta: (d / span) * total });
                }}
              />
            ) : null}
            <NodeView node={c} collapseAs={horizontal ? 'strip' : 'bar'} />
          </GroupCell>
        ))}
      </div>
    );
  }
  // sem abas, é o espaço vazio, à espera da próxima (e não recolhe)
  const active = node.sheets.find((s) => s.id === node.active) ?? node.sheets[0] ?? null;
  const isMax = max.maxNode === node.id && !!max.box;
  const collapsed = !!node.collapsed && !!collapseAs && !isMax && !!active;
  // recolhido lado a lado (ou sozinho numa coluna), a faixa fica de pé, com as abas deitadas
  const standing = collapsed && collapseAs === 'strip';
  const mainPanel = node.sheets.some((s) => s.main);
  // ampliado: o mesmo elemento passa a cobrir o corpo da janela (sem remontar a ficha)
  const maxStyle = isMax && max.box ? { position: 'fixed' as const, left: max.box.left, top: max.box.top, width: max.box.width, height: max.box.height, zIndex: 40 } : undefined;
  const foldButton =
    collapseAs && active ? (
      <button
        type="button"
        className="rd-dock__max rd-dock__fold"
        aria-expanded={!collapsed}
        aria-label={collapsed ? t('room.panel.expand') : t('room.panel.collapse')}
        title={collapsed ? t('room.panel.expand') : t('room.panel.collapse')}
        onClick={() => apply({ op: 'collapse', nodeId: node.id, collapsed: !collapsed })}
      >
        <Icon name={collapsed ? (collapseAs === 'strip' ? 'chevronRight' : 'chevronDown') : 'minus'} size={13} />
      </button>
    ) : null;
  const grab = menus.spaceHandlers(active, () => ref.current, node.id);
  return (
    <div
      ref={ref}
      data-node={node.id}
      role="region"
      // o espaço se chama pela aba à vista; o vazio, "Espaço vazio"
      {...(active ? { 'aria-labelledby': `${node.id}-${active.id}` } : { 'aria-label': t('room.panel.emptyLabel') })}
      className={`rd-dock__card${isMax ? ' rd-dock__card--max' : ''}${collapsed ? ` rd-dock__card--folded rd-dock__card--${collapseAs}` : ''}`}
      style={maxStyle}
    >
      {/*
       * o alto do espaço é a faixa de abas, sempre (até com uma só: é por ela que a aba se arrasta):
       * as abas, o "+", a parte vazia (que arrasta o espaço inteiro), "Recolher" e o ⋯. De pé, só
       * "Mostrar" e as abas deitadas; o espaço vazio tem o "+" e o × que o tira da janela
       */}
      <div className="rd-dock__tabs" {...(standing ? {} : { 'data-tabs-of': node.id })}>
        {standing ? foldButton : null}
        {active ? (
          <TabList active={active.id} label={t('room.tabs')} vertical={standing}>
            {node.sheets.map((s) => {
              const press = menus.tabHandlers(s, () => ref.current);
              return (
                <div
                  key={s.id}
                  data-sheet={s.id}
                  className={`rd-dock__tab${s.id === active.id ? ' rd-dock__tab--active' : ''}`}
                  {...press}
                  onPointerDown={(e) => {
                    press.onPointerDown(e);
                    edit.startTabDrag(node, s, e);
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={s.id === active.id}
                    className="rd-dock__tabBtn"
                    onPointerEnter={fullTitle}
                    onClick={() => apply({ op: 'activate', nodeId: node.id, sheetId: s.id })}
                    // dois cliques ampliam o espaço, e dois de novo o devolvem
                    onDoubleClick={() => max.toggle(node.id)}
                  >
                    <TabLabel sheet={s} id={`${node.id}-${s.id}`} />
                  </button>
                  {!s.main && !standing ? (
                    <button type="button" className="rd-dock__tabClose" aria-label={t('room.closeModule')} onClick={() => apply({ op: 'closeSheet', sheetId: s.id })}>
                      <Icon name="close" size={12} />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </TabList>
        ) : null}
        {!standing ? (
          <button
            type="button"
            className="rd-dock__tabAdd"
            aria-label={t('room.tab.add')}
            title={t('room.tab.add')}
            aria-haspopup="menu"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              edit.openAdd({ kind: 'into', nodeId: node.id }, { x: r.left, y: r.bottom + 2 });
            }}
          >
            <Icon name="plus" size={13} />
          </button>
        ) : null}
        {/*
         * a parte vazia da faixa: arrastar leva o espaço inteiro (ampliado, não), e o botão direito
         * (ou o dedo segurando) abre o menu do espaço. Fica fora da lista, para a aba não disputar
         * o segurar com ela
         */}
        <div
          className="rd-dock__grab"
          aria-hidden="true"
          title={active && !isMax ? t('room.layout.dragHint') : undefined}
          {...grab}
          onPointerDown={(e) => {
            grab.onPointerDown(e);
            if (!isMax) edit.startDrag(node, active, e);
          }}
        />
        {/*
         * o relógio da sessão, no espaço do chat da mesa (docs/spec/session-clock.md); fora da parte vazia,
         * que arrasta o espaço. As janelas próprias só têm cópias do chat, e a faixa de pé não tem lugar
         */}
        {mainPanel && !standing && !win ? <ClockPill /> : null}
        {!standing ? foldButton : null}
        {active && !standing ? (
          // o menu do espaço, à vista (docs/spec/room-menus.md)
          <button
            type="button"
            className="rd-dock__max"
            aria-label={t('room.panelMenu')}
            title={t('room.panelMenu')}
            aria-haspopup="menu"
            onMouseDown={() => moreToggle.onMouseDown('more')}
            onClick={(e) => {
              if (!moreToggle.opens('more')) return;
              const r = e.currentTarget.getBoundingClientRect();
              menus.open(active, { x: r.left, y: r.bottom + 2 }, ref.current, node.id);
            }}
          >
            <Icon name="more" size={13} strokeWidth={3.5} />
          </button>
        ) : null}
        {!active && !mainPanel ? (
          <button type="button" className="rd-dock__max rd-dock__close" aria-label={t('room.closePanel')} title={t('room.closePanel')} onClick={() => apply({ op: 'closePanel', nodeId: node.id })}>
            <Icon name="close" size={13} />
          </button>
        ) : null}
      </div>
      {menus.view}
      {/* recolhido, o módulo continua montado (a ficha não recarrega), só escondido */}
      <div className="rd-dock__body" hidden={collapsed} role="tabpanel">
        {active ? MODULES[active.classId].render(active) : <EmptyPanel inWindow={!!win} />}
      </div>
      {edit.editing && !collapsed
        ? (['left', 'right', 'top', 'bottom'] as const).map((side) => (
            <AddHere key={side} target={{ kind: 'split', nodeId: node.id, side }} label={t(`room.layout.add.${side}`)} className={`rd-layoutEdit__add rd-layoutEdit__add--${side}`} />
          ))
        : null}
    </div>
  );
}

/** O "+" do modo de edição: abre o menu dos módulos que podem ir para aquele lugar. */
function AddHere({ target, label, className }: { target: DropTarget; label: string; className: string }) {
  const edit = useContext(EditCtx);
  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      title={label}
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        edit.openAdd(target, { x: r.left, y: r.bottom + 2 });
      }}
    >
      <Icon name="plus" size={13} />
    </button>
  );
}

/**
 * A borda da janela: soltar ali (ou o "+") cria uma coluna, ou uma faixa, nova; numa janela própria,
 * divide a janela. No modo de edição, ela abre lugar e tem o "+"; fora dele, só enquanto uma aba (ou
 * um espaço) é arrastada, por cima dos espaços.
 */
function EdgeZone({ side }: { side: DropSide }) {
  const t = useT();
  const edit = useContext(EditCtx);
  const { win } = useDesk();
  return (
    <div className={`rd-layoutEdit__edge rd-layoutEdit__edge--${side}${edit.editing ? '' : ' rd-layoutEdit__edge--over'}`} data-drop-edge={side}>
      {edit.editing ? (
        <AddHere target={{ kind: 'edge', side, ...(win ? { win } : {}) }} label={t(win ? `room.layout.edge.window.${side}` : `room.layout.edge.${side}`)} className="rd-layoutEdit__add" />
      ) : null}
    </div>
  );
}

/** O espaço sem abas: o aviso de como pôr uma nele (numa janela própria, pelo "+"). */
function EmptyPanel({ inWindow }: { inWindow: boolean }) {
  const t = useT();
  return (
    <EmptyState className="rd-module__empty" icon="panels" title={t(inWindow ? 'room.panel.empty.window' : 'room.panel.empty')} />
  );
}

/** O centro sem nenhum espaço (o chat foi para uma lateral): recebe a aba e o espaço arrastados; no modo de edição, tem o "+". */
function EmptyCenter() {
  const t = useT();
  const edit = useContext(EditCtx);
  return (
    <div className="rd-room__client rd-room__client--empty" data-drop-center="">
      <p className="rd-room__emptyHint">{edit.editing ? t('room.layout.emptyCenter.editing') : t('room.layout.emptyCenter')}</p>
      {edit.editing ? <AddHere target={{ kind: 'center' }} label={t('room.layout.add.center')} className="rd-layoutEdit__add rd-layoutEdit__add--center" /> : null}
    </div>
  );
}

/**
 * Largura mínima de um nó: a do chat, a de um espaço aberto (o recolhido fica na tira dele), e a
 * soma dos filhos lado a lado. É o que impede um espaço novo ao lado dos outros (as configurações
 * junto do chat e da cena) de virar um fiapo: as laterais encolhem antes.
 */
function minWidthOf(n: DockNode): number {
  if (n.kind === 'content') return n.collapsed && n.sheets.length ? 0 : n.sheets.some((s) => s.classId === 'system/mainChat') ? CHAT_MIN : PANEL_MIN;
  const mins = n.children.map(minWidthOf);
  return n.orientation === 'horizontal' ? mins.reduce((a, b) => a + b, 0) + SPLITTER * (mins.length - 1) : Math.max(0, ...mins);
}

/** A célula de um filho do grupo; a do espaço recolhido fica do tamanho dele (a faixa deitada, ou de pé). */
function GroupCell({ grow, minWidth, folded, children }: { grow: number; minWidth: number; folded: boolean; children: React.ReactNode }) {
  return (
    <div className="rd-dock__cell" style={{ flex: folded ? 'none' : `${grow} 1 0`, minWidth }}>
      {children}
    </div>
  );
}
